import type { ComponentProps } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';
import { keepMovedFocus } from './focus-return';

/**
 * shadcn/ui's popover over Radix, with two product deltas:
 *
 * - NON-MODAL ONLY. A modal Radix popover locks scrolling through react-remove-scroll, a runtime `<style>` element
 *   that production's `style-src 'self'` refuses (see `dropdown-menu.tsx`). `modal` is removed from the props and
 *   pinned to `false`; an outside click, a focus leaving it and Escape still dismiss it, and Escape gives the
 *   focus back to the trigger.
 * - NO PORTAL by default. Rendered in place, the content stays inside a `ModalDialog` that opened it: that dialog's
 *   Tab loop reaches it, and its Escape comes after the popover's own (Radix answers Escape on the document in the
 *   capture phase and marks it `defaultPrevented`, which `ModalDialog` then leaves alone). Positioning is floating-ui
 *   writing `element.style` through the CSSOM, which the production CSP allows. `portal` puts it in `<body>`.
 * - A close gives the focus back to the trigger only when nothing else has taken it (`keepMovedFocus`).
 */
function Popover(props: Omit<ComponentProps<typeof PopoverPrimitive.Root>, 'modal'>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} modal={false} />;
}

const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;

function PopoverContent({
  className, align = 'center', sideOffset = 4, portal = false, onCloseAutoFocus, ...props
}: ComponentProps<typeof PopoverPrimitive.Content> & { portal?: boolean }) {
  const content = (
    <PopoverPrimitive.Content
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-[70] w-72 rounded-lg border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] p-4 text-[hsl(var(--h-card-foreground))] shadow-lg outline-none',
        // Grows from its trigger's side and fades out (styles/motion.css `.h-pop`); Radix waits for the exit.
        'h-pop',
        className,
      )}
      {...props}
      // Back to the trigger only if the focus was not already moved elsewhere while this faded out (`focus-return.ts`).
      onCloseAutoFocus={keepMovedFocus(onCloseAutoFocus)}
    />
  );
  return portal ? <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal> : content;
}

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
